// Import all lucide-react icons (local bindings for aliases + Icons object)
import {
  AlertCircle,
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpDown,
  ArrowUpRight,
  AtSign,
  BarChart2,
  BedSingle,
  Bell,
  BookOpen,
  Calendar,
  ChartColumnIncreasing,
  Check,
  CheckCheck,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
  Circle,
  CircleFadingPlus,
  CircleHelp,
  ClipboardCheck,
  ClipboardCopy,
  Clock,
  CloudDownload,
  CloudUpload,
  Compass,
  Copy,
  CreditCard,
  Cross,
  Crown,
  Database,
  DollarSign,
  Dot,
  ExternalLink,
  Eye,
  EyeOff,
  FileStackIcon,
  Gift,
  Globe,
  Hash,
  Heart,
  History,
  House,
  ImageIcon,
  Inbox,
  Info,
  Instagram,
  KeyRound,
  Library,
  Link,
  Link2,
  Loader,
  Loader2,
  LocateFixed,
  LogOut,
  MessageCircleMore as LucideMessageCircleMore,
  Mail,
  MapPin,
  MessageCircle,
  MessageCircleHeart,
  Mic,
  MoreHorizontal,
  MoreVertical,
  Package,
  PanelLeft,
  Paperclip,
  Pause,
  Pen,
  Percent,
  Phone,
  Play,
  PlayCircleIcon,
  Plus,
  PlusCircle,
  RefreshCw,
  RotateCcw,
  Route,
  Search,
  Send,
  ServerCrash,
  Settings,
  ShieldAlert,
  Smartphone,
  Smile,
  Sparkles,
  Sun,
  Moon,
  Monitor,
  Speech,
  Star,
  Stars,
  StopCircle,
  ThumbsDown,
  Ticket,
  Trash2,
  Upload,
  User,
  UserCheck,
  UserCircle2,
  UserMinus2,
  UserPlus2,
  UserRoundCheck,
  UserRoundPen,
  UserRoundX,
  UserSearch,
  Users,
  UsersIcon,
  Video,
  Wallet2,
  X,
  XCircle,
  ZapIcon,
} from 'lucide-react';

import dynamic from 'next/dynamic';

export type { LucideIcon } from 'lucide-react';

// Re-export lucide icons individually for tree-shaking
export {
  AlertCircle,
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpDown,
  ArrowUpRight,
  AtSign,
  BarChart2,
  BedSingle,
  Bell,
  BookOpen,
  Calendar as CalendarIcon,
  ChartColumnIncreasing,
  Check,
  CheckCheck,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
  Circle,
  CircleFadingPlus,
  CircleHelp,
  ClipboardCheck,
  ClipboardCopy,
  Clock,
  CloudDownload,
  CloudUpload,
  Compass,
  Copy,
  CreditCard,
  Cross,
  Crown,
  Database,
  DollarSign,
  Dot,
  ExternalLink,
  Eye,
  EyeOff,
  FileStackIcon,
  Gift,
  Globe,
  Hash,
  Heart,
  History,
  House,
  ImageIcon,
  Inbox,
  Info,
  Instagram,
  KeyRound,
  Library,
  Link,
  Link2,
  Loader,
  Loader2,
  LocateFixed,
  LogOut,
  LucideMessageCircleMore,
  Mail,
  MapPin,
  MessageCircle,
  MessageCircleHeart,
  Mic,
  MoreHorizontal,
  MoreVertical,
  Package,
  PanelLeft,
  Paperclip,
  Pause,
  Pen,
  Percent,
  Phone,
  Play,
  PlayCircleIcon,
  Plus,
  PlusCircle,
  RefreshCw,
  RotateCcw,
  Route,
  Search,
  Send,
  ServerCrash,
  Settings,
  ShieldAlert,
  Smartphone,
  Smile,
  Sparkles,
  Sun,
  Moon,
  Monitor,
  Speech,
  Star,
  Stars,
  StopCircle,
  ThumbsDown,
  Ticket,
  Trash2,
  Upload,
  User,
  UserCheck,
  UserCircle2,
  UserMinus2,
  UserPlus2,
  UserRoundCheck,
  UserRoundPen,
  UserRoundX,
  UserSearch,
  Users,
  UsersIcon,
  Video,
  Wallet2,
  X,
  XCircle,
  ZapIcon,
};
export const ClipboardCopyIcon = ClipboardCopy;
export const LoaderIcon = Loader;

// Custom SVG icons — lazy-loaded from separate files for code splitting
export const Spinner = dynamic(() => import('./Spinner'));
export const ExclamationCircle = dynamic(() => import('./ExclamationCircle'));
export const NoItem = dynamic(() => import('./NoItem'));
export const MagicWand = dynamic(() => import('./MagicWand'));
export const MapArrowSquare = dynamic(() => import('./MapArrowSquare'));
export const AgentEmpty = dynamic(() => import('./AgentEmpty'));
export const Logo = dynamic(() => import('./Logo'));
export const WhatsApp = dynamic(() => import('./WhatsApp'));
export const WhatsAppOutlineIcon = dynamic(() => import('./WhatsAppOutlineIcon'));
export const Zapier = dynamic(() => import('./Zapier'));
export const Make = dynamic(() => import('./Make'));
export const Claude = dynamic(() => import('./Claude'));
export const IG = dynamic(() => import('./IG'));
export const BulkFiles = dynamic(() => import('./BulkFiles'));
export const TiltedLink = dynamic(() => import('./TiltedLink'));
export const Trash3 = dynamic(() => import('./Trash3'));
export const UserSpeak = dynamic(() => import('./UserSpeak'));
export const UserSub = dynamic(() => import('./UserSub'));
export const MessageSquareCenter = dynamic(() => import('./MessageSquareCenter'));
export const DoubleConversation = dynamic(() => import('./DoubleConversation'));
export const DoubleConversationActive = dynamic(() => import('./DoubleConversationActive'));
export const Stack = dynamic(() => import('./Stack'));
export const StackActive = dynamic(() => import('./StackActive'));
export const Analytics = dynamic(() => import('./Analytics'));
export const Sequence = dynamic(() => import('./Sequence'));
export const SequenceActive = dynamic(() => import('./SequenceActive'));
export const Integration = dynamic(() => import('./Integration'));
export const IntegrationActive = dynamic(() => import('./IntegrationActive'));
export const SequenceStart = dynamic(() => import('./SequenceStart'));
export const SequenceAction = dynamic(() => import('./SequenceAction'));
export const SequenceCondition = dynamic(() => import('./SequenceCondition'));
export const SequenceDelay = dynamic(() => import('./SequenceDelay'));
export const BombEmoji = dynamic(() => import('./BombEmoji'));
export const MasterCard = dynamic(() => import('./MasterCard'));
export const LinkIcon = dynamic(() => import('./LinkIcon'));
export const Visa = dynamic(() => import('./Visa'));
export const AmericanExpress = dynamic(() => import('./AmericanExpress'));
export const FallingPaper = dynamic(() => import('./FallingPaper'));
export const General = dynamic(() => import('./General'));
export const TripleUser = dynamic(() => import('./TripleUser'));
export const Billing = dynamic(() => import('./Billing'));
export const Plans = dynamic(() => import('./Plans'));
export const PlansActive = dynamic(() => import('./PlansActive'));
export const SignOut = dynamic(() => import('./SignOut'));
export const MiniTelegram = dynamic(() => import('./MiniTelegram'));
export const AIStar = dynamic(() => import('./AIStar'));
export const AISequences = dynamic(() => import('./AISequences'));
export const CRMComingSoon = dynamic(() => import('./CRMComingSoon'));
export const CampaignSidebar = dynamic(() => import('./CampaignSidebar'));
export const AgentSidebar = dynamic(() => import('./AgentSidebar'));
export const AnalyticsSidebar = dynamic(() => import('./AnalyticsSidebar'));
export const SequencesSidebar = dynamic(() => import('./SequencesSidebar'));
export const LogOutOrsay = dynamic(() => import('./LogOutOrsay'));
export const PlusCircleSolid = dynamic(() => import('./PlusCircleSolid'));
export const Settings3 = dynamic(() => import('./Settings3'));
export const FineTuning = dynamic(() => import('./FineTuning'));
export const ActionStepIcon = dynamic(() => import('./ActionStepIcon'));
export const ConditionStepIcon = dynamic(() => import('./ConditionStepIcon'));
export const DelayStepIcon = dynamic(() => import('./DelayStepIcon'));
export const UserSpeakIcon = dynamic(() => import('./UserSpeakIcon'));
export const TiltedLink2 = dynamic(() => import('./TiltedLink2'));
export const Wallet3 = dynamic(() => import('./Wallet3'));
export const FileIcon = dynamic(() => import('./FileIcon'));
export const Unplug = dynamic(() => import('./Unplug'));
export const OpenFileIcon = dynamic(() => import('./OpenFileIcon'));
export const CloudUpload2 = dynamic(() => import('./CloudUpload2'));
export const Key3 = dynamic(() => import('./Key3'));
export const User3 = dynamic(() => import('./User3'));
export const PlayCircleIcon2 = dynamic(() => import('./PlayCircleIcon2'));
export const CheckCircle = dynamic(() => import('./CheckCircle'));
export const CloseCircle = dynamic(() => import('./CloseCircle'));
export const MenuDotsCircle = dynamic(() => import('./MenuDotsCircle'));
export const MinusCircle = dynamic(() => import('./MinusCircle'));
export const UsersGroupRounded = dynamic(() => import('./UsersGroupRounded'));
export const ArchiveMinimalistic = dynamic(() => import('./ArchiveMinimalistic'));
export const UnreadMessage = dynamic(() => import('./UnreadMessage'));
export const Refresh = dynamic(() => import('./Refresh'));
export const SpeechToAudio = dynamic(() => import('./SpeechToAudio'));
export const CreateAgentFlow = dynamic(() => import('./CreateAgentFlow'));
export const CreateAgentFlow2 = dynamic(() => import('./CreateAgentFlow2'));
export const DocumentText = dynamic(() => import('./DocumentText'));
export const Stripe = dynamic(() => import('./Stripe'));
export const Crown2 = dynamic(() => import('./Crown2'));
export const InboxIn = dynamic(() => import('./InboxIn'));
export const InboxOut = dynamic(() => import('./InboxOut'));
export const PeopleNearby = dynamic(() => import('./PeopleNearby'));
export const MessageCircleHeart2 = dynamic(() => import('./MessageCircleHeart2'));
export const ScanFaceCircle = dynamic(() => import('./ScanFaceCircle'));
export const FunnySquareFace = dynamic(() => import('./FunnySquareFace'));
export const FlagFR = dynamic(() => import('./FlagFR'));
export const FlagEN = dynamic(() => import('./FlagEN'));

/**
 * IconMap — only icons accessed dynamically via `Icons[key]`.
 * For static usage, import named exports directly:
 *   import { Check, ArrowUp } from '@/components/ui/icons/Icons';
 */
export const Icons = {
  // Sidebar & navigation
  AgentSidebar,
  AnalyticsSidebar,
  Billing,
  CampaignSidebar,
  DoubleConversation,
  FineTuning,
  Gift,
  Instagram,
  Key3,
  SequencesSidebar,
  TripleUser,
  User3,

  // Sequence triggers
  BookOpen,
  Calendar,
  CircleFadingPlus,
  Heart,
  LucideMessageCircleMore,
  MessageCircle,
  MessageCircleHeart,
  MessageSquareCenter,
  Send,
  UserCheck,
  UserSearch,
  UserSpeak,
  UserSub,

  // Sequence actions
  UserPlus2,
  UserRoundX,

  // Analytics metrics
  ArchiveMinimalistic,
  CheckCircle,
  CloseCircle,
  MenuDotsCircle,
  MessageCircleHeart2,
  MinusCircle,
  PeopleNearby,
  PlusCircle,
  ScanFaceCircle,
  UnreadMessage,
  UserRoundCheck,
  UserRoundPen,
  UsersGroupRounded,
  X,

  // Integrations
  Claude,
  CloudDownload,
  IG,
  MagicWand,
  Make,
  Mic,
  SpeechToAudio,
  Speech,
  WhatsApp,
  Zapier,

  // Misc
  Sparkles,
};
